import * as React from "react";

export interface AnchoredOverlayPosition {
  side: "top" | "bottom";
  left: number;
  width: number;
  maxHeight: number;
  top?: number;
  bottom?: number;
}

interface UseAnchoredOverlayOptions {
  open: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
  width: number;
  maxHeight: number;
  preferredSide?: "top" | "bottom";
  align?: "start" | "end";
  gap?: number;
  viewportPadding?: number;
}

export function useAnchoredOverlay({
  open,
  anchorRef,
  width,
  maxHeight,
  preferredSide = "top",
  align = "start",
  gap = 8,
  viewportPadding = 8,
}: UseAnchoredOverlayOptions): AnchoredOverlayPosition | null {
  const [position, setPosition] = React.useState<AnchoredOverlayPosition | null>(null);

  React.useLayoutEffect(() => {
    if (!open) {
      return undefined;
    }

    const updatePosition = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      const resolvedWidth = Math.min(width, Math.max(0, window.innerWidth - viewportPadding * 2));
      const availableTop = Math.max(0, rect.top - gap - viewportPadding);
      const availableBottom = Math.max(0, window.innerHeight - rect.bottom - gap - viewportPadding);
      const preferredAvailable = preferredSide === "top" ? availableTop : availableBottom;
      const alternateAvailable = preferredSide === "top" ? availableBottom : availableTop;
      const side =
        preferredAvailable >= Math.min(maxHeight, alternateAvailable)
          ? preferredSide
          : preferredSide === "top"
            ? "bottom"
            : "top";
      const availableHeight = side === "top" ? availableTop : availableBottom;
      const desiredLeft = align === "end" ? rect.right - resolvedWidth : rect.left;
      const maxLeft = Math.max(
        viewportPadding,
        window.innerWidth - viewportPadding - resolvedWidth
      );
      const left = Math.min(Math.max(viewportPadding, desiredLeft), maxLeft);

      setPosition({
        side,
        left,
        width: resolvedWidth,
        maxHeight: Math.min(maxHeight, availableHeight),
        ...(side === "top"
          ? { bottom: window.innerHeight - rect.top + gap }
          : { top: rect.bottom + gap }),
      });
    };

    updatePosition();
    const deferredUpdate = window.setTimeout(updatePosition, 0);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.clearTimeout(deferredUpdate);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [align, anchorRef, gap, maxHeight, open, preferredSide, viewportPadding, width]);

  return open ? position : null;
}
